import React, { useEffect, useRef, useState } from "react";
import { useSite } from "@/context/SiteContext";
import { resolveImg } from "@/lib/api";

// Full-screen intro that plays once per session on the homepage. The real hero
// video plays behind the logo in black & white, then the panel lifts away to
// reveal the (also black & white) live hero. Respects reduced-motion.
export function IntroSplash() {
  const { settings, home } = useSite();
  const [hidden, setHidden] = useState(false);
  const [lift, setLift] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const seen = sessionStorage.getItem("p302_intro");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (seen || reduce) {
      setHidden(true);
      sessionStorage.setItem("p302_intro", "1");
      return;
    }
    const html = document.documentElement;
    const body = document.body;
    const toTop = () => window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    const unlock = () => {
      Object.assign(body.style, { overflow: "", position: "", top: "", width: "" });
      html.style.overflow = "";
    };
    toTop();
    Object.assign(body.style, { overflow: "hidden", position: "fixed", top: "0", width: "100%" });
    html.style.overflow = "hidden";
    const block = (e) => e.preventDefault();
    window.addEventListener("touchmove", block, { passive: false });
    const v = videoRef.current;
    if (v) {
      v.muted = true;
      v.defaultMuted = true;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
    const t1 = setTimeout(() => setLift(true), 2400);
    const t2 = setTimeout(() => {
      setHidden(true);
      window.removeEventListener("touchmove", block);
      unlock();
      toTop();
      sessionStorage.setItem("p302_intro", "1");
    }, 3550);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("touchmove", block);
      unlock();
    };
  }, []);

  if (hidden) return null;

  const videoSrc = home?.hero_video_url;
  const videoWebm = home?.hero_video_webm_url;
  const poster = home?.hero_poster || home?.hero_slides?.[0];
  const logoLight = settings?.logo_light_url;
  const logoSrc = logoLight || settings?.logo_url;
  const invert = !logoLight && !!settings?.logo_url;

  return (
    <div
      data-testid="intro-splash"
      className="fixed inset-0 z-[100] bg-espresso flex flex-col items-center justify-center overflow-hidden touch-none"
      style={{
        transform: lift ? "translateY(-100%)" : "translateY(0)",
        transition: "transform 1.15s cubic-bezier(0.76, 0, 0.24, 1)",
      }}
    >
      {videoSrc ? (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={resolveImg(poster)}
          data-testid="intro-video"
          style={{
            filter: "grayscale(1) contrast(1.05) brightness(0.92)",
          }}
        >
          {videoWebm && <source src={resolveImg(videoWebm)} type="video/webm" />}
          <source src={resolveImg(videoSrc)} type="video/mp4" />
        </video>
      ) : poster ? (
        <div
          className="absolute inset-0 bg-cover bg-center kenburns"
          style={{
            backgroundImage: `url(${resolveImg(poster)})`,
            filter: "grayscale(1)",
          }}
        />
      ) : null}
      <div className="absolute inset-0 bg-gradient-to-b from-espresso/70 via-espresso/55 to-espresso/80" />
      <div className="intro-logo relative flex flex-col items-center px-8">
        {logoSrc ? (
          <img src={resolveImg(logoSrc)} alt="Psalm 30:2 Chiropractic" className="h-44 sm:h-56 lg:h-72 w-auto object-contain" style={{ filter: invert ? "invert(1)" : "none" }} />
        ) : (
          <div className="text-center text-ivory">
            <div className="font-serif text-6xl lg:text-8xl tracking-tight">Psalm 30:2</div>
            <div className="text-xs uppercase tracking-[0.4em] text-ivory/60 mt-3">Chiropractic</div>
          </div>
        )}
        <span className="intro-line h-px bg-brass mt-8" />
      </div>
    </div>
  );
}
